/**
 * Partner Service for Broker Portal
 * Provides partner and broker information
 */

import store from '@/store'
import partnerRepository, { Broker, Partner } from '../repositories/partner.repository'

export interface BrokerInfo {
  id: number
  name: string
  logoUrl: string | null
  partner: Partner
}

class PartnerService {
  /**
   * Get broker information for the current partner
   */
  async getBrokerInfo(): Promise<BrokerInfo | null> {
    const partnerId = store.getters['broker/partnerId']

    if (!partnerId) {
      console.warn('No partner ID available')
      return null
    }

    try {
      const broker = await partnerRepository.getBrokerInfo(partnerId)

      if (!broker) {
        return null
      }

      return {
        id: broker.id,
        name: broker.name,
        logoUrl: broker.logoUrl,
        partner: broker.partner
      }
    } catch (error) {
      console.error('Failed to get broker info:', error)
      return null
    }
  }

  /**
   * Get the partner logo URL
   * Tries to get from partnerBrand first, then falls back to partner logo
   */
  getPartnerLogoUrl(broker: BrokerInfo | null): string | null {
    if (!broker?.partner) return null

    const { partner } = broker

    // Priority: partnerBrand.responsiveLogo > partner.responsiveLogoUrl > partner.fullLogoUrl
    if (partner.partnerBrand?.responsiveLogo) {
      return partner.partnerBrand.responsiveLogo
    }

    if (partner.responsiveLogoUrl) {
      return partner.responsiveLogoUrl
    }

    if (partner.fullLogoUrl) {
      return partner.fullLogoUrl
    }

    return null
  }

  /**
   * Get the partner/broker display name
   */
  getBrokerDisplayName(broker: BrokerInfo | null): string {
    if (!broker) return ''

    // Use partner shortName or fullName, fallback to broker name
    if (broker.partner?.shortName) {
      return broker.partner.shortName
    }

    if (broker.partner?.fullName) {
      return broker.partner.fullName
    }

    return broker.name
  }
}

export default new PartnerService()
